import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { StudyItem } from '../../../services/studies-api.service';
import { environment } from 'src/environments/environment';

export interface DicomViewerDialogData {
  study: StudyItem;
}

@Component({
    selector: 'app-dicom-viewer-dialog',
    templateUrl: './dicom-viewer-dialog.component.html',
    styleUrls: ['./dicom-viewer-dialog.component.scss'],
    standalone: false
})
export class DicomViewerDialogComponent implements AfterViewInit, OnDestroy {
  loading = true;
  error: string | null = null;
  isFullscreen = false;

  instanceIds: string[] = [];
  currentIndex = 0;
  imageUrl: SafeUrl | null = null;

  private objectUrls: string[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DicomViewerDialogData,
    private dialogRef: MatDialogRef<DicomViewerDialogComponent>,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
    if (this.isFullscreen) {
      this.dialogRef.updateSize('100vw', '100vh');
      this.dialogRef.addPanelClass('fullscreen');
    } else {
      this.dialogRef.updateSize('860px', '');
      this.dialogRef.removePanelClass('fullscreen');
    }
  }

  get currentImageNumber(): number { return this.currentIndex + 1; }
  get totalImages(): number { return this.instanceIds.length; }

  async ngAfterViewInit() {
    try {
      const uid = this.data.study.studyInstanceUid;
      const instances = await firstValueFrom(this.http
        .get<{ instanceUid: string }[]>(`${environment.bffBaseUrl}/wado-rs/studies/${uid}/instances`));

      if (!instances || instances.length === 0) {
        this.error = 'No instances found for this study.';
        this.loading = false;
        this.cdr.detectChanges();
        return;
      }

      this.instanceIds = instances.map(i => i.instanceUid);
      await this.loadCurrentImage();
    } catch (e) {
      console.error('[dicom-viewer] Failed to load study:', e);
      this.error = 'Failed to load study. Please try again.';
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  async loadCurrentImage() {
    this.loading = true;
    this.imageUrl = null;
    this.cdr.detectChanges();

    try {
      const instanceId = this.instanceIds[this.currentIndex];
      const blob = await firstValueFrom(this.http
        .get(`${environment.bffBaseUrl}/wado-rs/instances/${instanceId}/rendered`, { responseType: 'blob' }));

      const objectUrl = URL.createObjectURL(blob as Blob);
      this.objectUrls.push(objectUrl);
      this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
      this.loading = false;
      this.cdr.detectChanges();
    } catch (e) {
      console.error('[dicom-viewer] Failed to render image:', e);
      this.error = 'Failed to load image. Please try again.';
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  async goToPrevious() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      await this.loadCurrentImage();
    }
  }

  async goToNext() {
    if (this.currentIndex < this.instanceIds.length - 1) {
      this.currentIndex++;
      await this.loadCurrentImage();
    }
  }

  ngOnDestroy() {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
  }
}
